import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { List, X, CaretDown } from "@phosphor-icons/react";
import { useState } from "react";
import { BRAND_LOGO, BRAND_NAME } from "@/lib/brand";
import EditableSection from "@/components/blocks/EditableSection";
import PostcodeLookupBar from "@/components/PostcodeLookupBar";

export default function PublicHeader() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  return (
    <>
      <EditableSection slug="header-banner" />
      <PostcodeLookupBar />
      <header data-testid="public-header" className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-12 lg:h-14 w-auto group-hover:scale-105 transition-transform" />
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-stone-700">
          <Link to="/" className="hover:text-[#f38838] transition-colors">Home</Link>
          <Link to="/services" className="hover:text-[#f38838] transition-colors">Services</Link>
          <div
            className="relative"
            onMouseEnter={() => setCoursesOpen(true)}
            onMouseLeave={() => setCoursesOpen(false)}
          >
            <button className="hover:text-[#f38838] transition-colors inline-flex items-center gap-1" data-testid="nav-courses">
              Courses <CaretDown size={12} weight="bold" />
            </button>
            {coursesOpen && (
              <div className="absolute top-full left-0 pt-2 w-56">
                <div className="card-pp p-2 shadow-xl">
                  <NavLink to="/courses/in-person" className="block px-3 py-2 rounded-md hover:bg-[#f38838]/10 hover:text-[#f38838] text-sm">In-Person Classes</NavLink>
                  <NavLink to="/courses/online" className="block px-3 py-2 rounded-md hover:bg-[#f38838]/10 hover:text-[#f38838] text-sm">Online Courses</NavLink>
                </div>
              </div>
            )}
          </div>
          <div
            className="relative"
            onMouseEnter={() => setLocationsOpen(true)}
            onMouseLeave={() => setLocationsOpen(false)}
          >
            <button onClick={() => setLocationsOpen(!locationsOpen)} className="hover:text-[#f38838] transition-colors flex items-center gap-1" data-testid="nav-areas">
              Service Areas <CaretDown size={14} />
            </button>
            {locationsOpen && (
              <div className="absolute top-full left-0 pt-2 w-64 z-50">
                <div className="card-pp p-2 shadow-xl">
                  <NavLink to="/service-areas/mudjimba" className="block px-3 py-2 rounded-md hover:bg-[#f38838]/10 hover:text-[#f38838] text-sm" data-testid="nav-hall-mudjimba">Mudjimba Hall (North)</NavLink>
                  <NavLink to="/service-areas/buddina" className="block px-3 py-2 rounded-md hover:bg-[#f38838]/10 hover:text-[#f38838] text-sm" data-testid="nav-hall-kawana">Kawana Hall (Central)</NavLink>
                  <NavLink to="/service-areas/eudlo" className="block px-3 py-2 rounded-md hover:bg-[#f38838]/10 hover:text-[#f38838] text-sm" data-testid="nav-hall-eudlo">Eudlo Hall (Hinterland)</NavLink>
                  <div className="border-t border-stone-100 my-1" />
                  <NavLink to="/service-areas" className="block px-3 py-2 rounded-md hover:bg-[#f38838]/10 hover:text-[#f38838] text-sm font-semibold" data-testid="nav-all-areas">All Sunshine Coast areas →</NavLink>
                </div>
              </div>
            )}
          </div>
          <Link to="/about" className="hover:text-[#f38838] transition-colors">About</Link>
          <Link to="/team" className="hover:text-[#f38838] transition-colors">Team</Link>
          <Link to="/tips" className="hover:text-[#f38838] transition-colors">Tips</Link>
          <Link to="/contact" className="hover:text-[#f38838] transition-colors">Contact</Link>
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to={user.role === "client" ? "/portal" : "/admin"}
                className="btn-primary text-sm"
                data-testid="header-portal-link"
              >
                {user.role === "client" ? "Client Portal" : "Dashboard"}
              </Link>
              <button onClick={logout} className="text-sm text-stone-600 hover:text-stone-900" data-testid="header-logout">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-stone-700 hover:text-[#f38838]" data-testid="header-login">
                Sign in
              </Link>
              <Link to="/services" className="btn-primary text-sm" data-testid="header-register">
                Get Started
              </Link>
            </>
          )}
        </div>
        <button className="md:hidden text-stone-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white px-6 py-4 space-y-3 text-stone-700">
          <Link to="/" onClick={() => setOpen(false)} className="block">Home</Link>
          <Link to="/services" onClick={() => setOpen(false)} className="block">Services</Link>
          <Link to="/courses/in-person" onClick={() => setOpen(false)} className="block pl-2 text-sm">↳ In-Person Classes</Link>
          <Link to="/courses/online" onClick={() => setOpen(false)} className="block pl-2 text-sm">↳ Online Courses</Link>
          <Link to="/service-areas" onClick={() => setOpen(false)} className="block">Service Areas</Link>
          <Link to="/about" onClick={() => setOpen(false)} className="block">About</Link>
          <Link to="/team" onClick={() => setOpen(false)} className="block">Team</Link>
          <Link to="/tips" onClick={() => setOpen(false)} className="block">Tips</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="block">Contact</Link>
          {user ? (
            <Link to={user.role === "client" ? "/portal" : "/admin"} className="block btn-primary text-sm w-fit">Open Portal</Link>
          ) : (
            <div className="flex gap-3 pt-2">
              <Link to="/login" className="text-sm">Sign in</Link>
              <Link to="/services" className="btn-primary text-sm">Get Started</Link>
            </div>
          )}
        </div>
      )}
      </header>
    </>
  );
}
